"use client";

/**
 * ChatCommandMenu Component
 *
 * A sectioned "/" command menu that opens when the user types "/" at the
 * start of the chat input or activates a "/" affordance button.
 *
 * Reuses the fixed-position popup pattern established by MentionAutocomplete:
 * positioned near the caret, keyboard-navigable with ArrowUp/Down + Enter + Esc.
 */

import React, { useEffect, useRef, useState } from "react";
import { Link, Switch, Tooltip, TooltipTrigger, TooltipContent } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { logger } from "@/telemetry";
import { FeatureCardProps } from "@/components/Chat/FeatureCards";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ChatCommandMenuItem {
  id: string;
  /** Translated label — caller must resolve via t() before passing. */
  label: string;
  /** Iconify icon identifier. */
  icon: string;
  /** When true the row renders a toggle switch instead of a plain action. */
  isToggle?: boolean;
  /** Current toggle state (only relevant when isToggle is true). */
  toggleValue?: boolean;
  /** Called with the new boolean value when the toggle changes. */
  onToggle?: (val: boolean) => void;
  /** Called when an action item is activated (click or Enter). */
  onSelect?: () => void;
  /** Prevents activation of the item. */
  isDisabled?: boolean;
  /** Human-readable explanation shown via title attribute when disabled. */
  disabledReason?: string;
  /**
   * When set, the row renders as a skill shortcut: `/{key}` in medium weight
   * above a muted helper line (this description text).
   */
  skillKey?: string;
  /** Muted helper text shown below the skill key in the row. */
  skillDescription?: string;
  /**
   * Muted helper line shown below the label for non-skill rows (e.g. workflow
   * rows show the workflow name on top and its description here).
   */
  description?: string;
  /** When true, the row shows a tick to indicate it is the active selection. */
  isCurrent?: boolean;
  /**
   * When true, activating the row does NOT close the menu (used for drill-in
   * entries that open a sub-view, e.g. "Workflows").
   */
  keepOpenOnSelect?: boolean;
  /** When true, a hairline divider is rendered above the row. */
  dividerBefore?: boolean;
}

export interface ChatCommandMenuSection {
  id: string;
  /** Translated section label — caller resolves via t(). */
  label: string;
  items: ChatCommandMenuItem[];
}

export interface ChatCommandMenuProps {
  /** Whether the menu is currently visible. */
  visible: boolean;
  /** Fixed-position coordinates for the popup. */
  position: { top: number; left: number };

  // --- Tool states ---
  isWebSearchEnabled: boolean;
  isKnowledgeHubEnabled: boolean;
  /** Whether the bottom-right model selector is currently shown. */
  modelSelectorEnabled: boolean;

  // --- Visibility flags (permission gates) ---
  showWebSearch: boolean;
  showKnowledgeHub: boolean;
  /** Whether to show the Model Selector toggle row in the Tools section. */
  showModelSelector: boolean;
  showPrompts: boolean;
  showPrompto: boolean;
  showConfiguration: boolean;

  // --- Disabled states ---
  isConfigurationDisabled: boolean;
  isPromptLocked: boolean;

  // --- Workflows (in-menu workflow picker) ---
  /** Workflows the user can switch the chat to. When empty the section is hidden. */
  workflows: { id: string; name: string; description?: string; icon?: string }[];
  /** Whether to render the Workflows section at all (permission / availability gate). */
  showWorkflows: boolean;
  /** Id of the workflow currently active in the chat, marked with a tick. */
  activeWorkflowId?: string | null;
  /** Brand-resolved heading for the Workflows section (falls back to a localised default). */
  workflowsLabel?: string;

  // --- Skills (prompt shortcuts) ---
  /** Prompt shortcut cards surfaced as `/name` skill rows. When empty the section is hidden. */
  features: FeatureCardProps[];
  /**
   * Text typed after the leading `/` — used to filter skill rows by prefix or
   * substring match on `key` and `title`. Empty string shows all skills.
   */
  skillQuery: string;

  // --- Handlers ---
  onWebSearchToggle: (val: boolean) => void;
  onKnowledgeHubToggle: () => void;
  /** Called with the new boolean when the user toggles the model selector row. */
  onModelSelectorToggle: (val: boolean) => void;
  onBrowsePrompts: () => void;
  onPrompto: () => void;
  onOpenConfiguration: () => void;
  /** Called with the skill's title when the user activates a skill row. */
  onSelectSkill: (title: string) => void;
  /** Called with a workflow id when the user activates a workflow row. */
  onSelectWorkflow: (workflowId: string) => void;
  /** Navigate to the full Workflows index ("Browse all workflows" footer action). */
  onBrowseAllWorkflows: () => void;
  /** Create a new workflow. When omitted, the "New workflow" row is hidden (RBAC gate). */
  onCreateWorkflow?: () => void;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Renders the keyboard shortcut badge used in the footer. */
const KbdHint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <kbd className="px-1 py-0.5 bg-default-100 rounded text-[10px] font-mono">{children}</kbd>
);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * ChatCommandMenu renders a fixed-position, sectioned command palette that
 * surfaces tools, prompts, model selection, and configuration in one place.
 *
 * Only ACTION items (non-toggle) participate in keyboard navigation so that
 * toggle rows remain mouse/pointer-first without breaking arrow-key flow.
 */
export const ChatCommandMenu: React.FC<ChatCommandMenuProps> = ({
  visible,
  position,
  isWebSearchEnabled,
  isKnowledgeHubEnabled,
  modelSelectorEnabled,
  showWebSearch,
  showKnowledgeHub,
  showModelSelector,
  showPrompts,
  showPrompto,
  showConfiguration,
  isConfigurationDisabled,
  isPromptLocked,
  workflows,
  showWorkflows,
  activeWorkflowId,
  workflowsLabel,
  features,
  skillQuery,
  onWebSearchToggle,
  onKnowledgeHubToggle,
  onModelSelectorToggle,
  onBrowsePrompts,
  onPrompto,
  onOpenConfiguration,
  onSelectSkill,
  onSelectWorkflow,
  onBrowseAllWorkflows,
  onCreateWorkflow,
  onClose,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  // Index into the flat list of ACTION items (toggle rows excluded).
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Which view the menu is showing: the main sections, or the Workflows drill-in.
  const [view, setView] = useState<"main" | "workflows">("main");

  // ---------------------------------------------------------------------------
  // Build section descriptors from props
  // ---------------------------------------------------------------------------

  const sections: ChatCommandMenuSection[] = React.useMemo(() => {
    const built: ChatCommandMenuSection[] = [];

    // Skills section — prompt shortcuts surfaced as `/name` rows.
    // Only rendered when the caller provides at least one feature card.
    if (features.length > 0) {
      const query = skillQuery.trim().toLowerCase();
      const filtered = features.filter((f) => {
        if (!query) return true;

        return f.key.toLowerCase().includes(query) || f.title.toLowerCase().includes(query);
      });

      if (filtered.length > 0) {
        built.push({
          id: "skills",
          label: t("chatCommandMenu.sections.skills"),
          items: filtered.map((f) => ({
            id: `skill-${f.key}`,
            label: f.title,
            icon: f.icon || "solar:magic-stick-3-linear",
            skillKey: f.key,
            skillDescription: f.descriptions[0] ?? f.title,
            onSelect: () => onSelectSkill(f.title),
          })),
        });
      }
    }

    // Tools section
    const toolItems: ChatCommandMenuItem[] = [];

    if (showWebSearch) {
      toolItems.push({
        id: "web-search",
        label: t("chatCommandMenu.items.webSearch"),
        icon: "solar:global-linear",
        isToggle: true,
        toggleValue: isWebSearchEnabled,
        onToggle: onWebSearchToggle,
      });
    }

    if (showKnowledgeHub) {
      toolItems.push({
        id: "knowledge-hub",
        label: t("chatCommandMenu.items.knowledgeHub"),
        icon: "solar:database-linear",
        isToggle: true,
        toggleValue: isKnowledgeHubEnabled,
        onToggle: onKnowledgeHubToggle,
      });
    }

    if (showModelSelector) {
      toolItems.push({
        id: "model-selector",
        label: t("chatCommandMenu.items.modelSelector"),
        icon: "solar:cpu-bolt-linear",
        isToggle: true,
        toggleValue: modelSelectorEnabled,
        onToggle: onModelSelectorToggle,
      });
    }

    if (toolItems.length > 0) {
      built.push({
        id: "tools",
        label: t("chatCommandMenu.sections.tools"),
        items: toolItems,
      });
    }

    // Prompts section
    const promptItems: ChatCommandMenuItem[] = [];

    if (showPrompts) {
      promptItems.push({
        id: "browse-prompts",
        label: t("chatCommandMenu.items.browsePrompts"),
        icon: "solar:document-text-linear",
        isDisabled: isPromptLocked,
        onSelect: onBrowsePrompts,
      });
    }

    if (showPrompto) {
      promptItems.push({
        id: "prompto",
        label: t("chatCommandMenu.items.prompto"),
        icon: "solar:magic-stick-linear",
        onSelect: onPrompto,
      });
    }

    if (promptItems.length > 0) {
      built.push({
        id: "prompts",
        label: t("chatCommandMenu.sections.prompts"),
        items: promptItems,
      });
    }

    // Workflows drill-in entry — a single "Workflows" row that expands into a
    // dedicated sub-view (keeps the main menu uncluttered). The headerless
    // section renders just the row; the list + footer actions live in the
    // sub-view (see workflowSubItems below).
    if (showWorkflows && workflows.length > 0) {
      built.push({
        id: "workflows-entry",
        label: "",
        items: [
          {
            id: "workflows-open",
            label: workflowsLabel || t("chatCommandMenu.sections.workflows"),
            icon: "solar:widget-2-linear",
            keepOpenOnSelect: true,
            onSelect: () => setView("workflows"),
          },
        ],
      });
    }

    // Configuration section
    if (showConfiguration) {
      built.push({
        id: "configuration",
        label: t("chatCommandMenu.sections.configuration"),
        items: [
          {
            id: "configuration",
            label: t("chatCommandMenu.items.configuration"),
            icon: "solar:settings-linear",
            isDisabled: isConfigurationDisabled,
            onSelect: onOpenConfiguration,
          },
        ],
      });
    }

    return built;
  }, [
    t,
    features,
    skillQuery,
    showWebSearch,
    showKnowledgeHub,
    showModelSelector,
    showPrompts,
    showPrompto,
    showConfiguration,
    showWorkflows,
    workflows,
    workflowsLabel,
    isWebSearchEnabled,
    isKnowledgeHubEnabled,
    modelSelectorEnabled,
    isConfigurationDisabled,
    isPromptLocked,
    onWebSearchToggle,
    onKnowledgeHubToggle,
    onModelSelectorToggle,
    onBrowsePrompts,
    onPrompto,
    onOpenConfiguration,
    onSelectSkill,
  ]);

  // Items shown inside the Workflows sub-view: the filterable workflow list
  // followed by the "Browse all workflows" / "New workflow" footer actions.
  const workflowSubItems: ChatCommandMenuItem[] = React.useMemo(() => {
    if (!showWorkflows) return [];

    const wq = skillQuery.trim().toLowerCase();
    const filtered = workflows.filter((w) => {
      if (!wq) return true;

      return w.id.toLowerCase().includes(wq) || w.name.toLowerCase().includes(wq);
    });

    const items: ChatCommandMenuItem[] = filtered.map((w) => ({
      id: `workflow-${w.id}`,
      label: w.name,
      // One neutral glyph keeps the list uniform; per-workflow icons are noisy.
      icon: "solar:widget-2-linear",
      description: w.description || undefined,
      isCurrent: w.id === activeWorkflowId,
      onSelect: () => onSelectWorkflow(w.id),
    }));

    // Footer actions, mirroring the skills sub-view ("Browse all" / "Add").
    items.push({
      id: "workflows-browse-all",
      label: t("chatCommandMenu.workflows.browseAll"),
      icon: "solar:layers-minimalistic-linear",
      dividerBefore: true,
      onSelect: onBrowseAllWorkflows,
    });

    if (onCreateWorkflow) {
      items.push({
        id: "workflows-create",
        label: t("chatCommandMenu.workflows.create"),
        icon: "solar:add-square-linear",
        onSelect: onCreateWorkflow,
      });
    }

    return items;
  }, [
    showWorkflows,
    skillQuery,
    workflows,
    activeWorkflowId,
    t,
    onSelectWorkflow,
    onBrowseAllWorkflows,
    onCreateWorkflow,
  ]);

  // Flat list of only the ACTION items (non-toggle, non-disabled) for keyboard
  // nav. View-aware: the Workflows sub-view navigates its own items.
  const actionItems = React.useMemo(() => {
    const source = view === "workflows" ? workflowSubItems : sections.flatMap((s) => s.items);

    return source.filter((item) => !item.isToggle && !item.isDisabled);
  }, [view, workflowSubItems, sections]);

  // Reset selection when the menu opens, the view changes, or the list changes.
  useEffect(() => {
    setSelectedIndex(0);
  }, [visible, view, actionItems.length]);

  // Reset to the main view whenever the menu is reopened.
  useEffect(() => {
    if (!visible) setView("main");
  }, [visible]);

  // Scroll the highlighted item into view whenever selection changes.
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current.querySelector<HTMLElement>(
      `[data-action-id="${actionItems[selectedIndex]?.id}"]`,
    );

    if (el) {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex, actionItems]);

  // ---------------------------------------------------------------------------
  // Keyboard navigation
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            actionItems.length === 0 ? 0 : (prev + 1) % actionItems.length,
          );
          break;

        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) =>
            actionItems.length === 0 ? 0 : (prev - 1 + actionItems.length) % actionItems.length,
          );
          break;

        case "Enter": {
          e.preventDefault();
          const active = actionItems[selectedIndex];

          if (active) {
            logger.info("ChatCommandMenu: item activated via keyboard", { id: active.id });
            active.onSelect?.();
            if (!active.keepOpenOnSelect) onClose();
          }
          break;
        }

        case "Escape":
          e.preventDefault();
          // In the Workflows sub-view, Esc steps back to the main menu first.
          if (view === "workflows") {
            setView("main");
          } else {
            onClose();
          }
          break;

        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [visible, view, actionItems, selectedIndex, onClose]);

  // ---------------------------------------------------------------------------
  // Early return
  // ---------------------------------------------------------------------------

  if (!visible) return null;

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const renderToggleRow = (item: ChatCommandMenuItem) => (
    <div
      key={item.id}
      aria-checked={item.toggleValue}
      className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-default-100 transition-colors select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset rounded"
      role="menuitemcheckbox"
      tabIndex={0}
      onClick={() => item.onToggle?.(!item.toggleValue)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          item.onToggle?.(!item.toggleValue);
        }
      }}
    >
      {/* Leading icon */}
      <Icon className="w-4 h-4 text-default-500 shrink-0" icon={item.icon} />

      {/* Label */}
      <span className="flex-1 text-sm text-default-700">{item.label}</span>

      {/* Toggle — stopPropagation prevents the row onClick from double-firing */}
      <Switch
        isSelected={item.toggleValue}
        size="sm"
        onChange={(val: boolean) => {
          item.onToggle?.(val);
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Switch.Control>
          <Switch.Thumb />
        </Switch.Control>
      </Switch>
    </div>
  );

  const renderActionRow = (item: ChatCommandMenuItem, isActive: boolean) => (
    <div
      key={item.id}
      aria-disabled={item.isDisabled}
      className={[
        "flex items-center gap-3 px-3 py-2 transition-colors select-none",
        item.isDisabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:bg-default-100",
        isActive && !item.isDisabled ? "bg-default-100" : "",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset rounded",
      ]
        .filter(Boolean)
        .join(" ")}
      data-action-id={item.id}
      role="menuitem"
      tabIndex={-1}
      title={item.isDisabled && item.disabledReason ? item.disabledReason : undefined}
      onClick={() => {
        if (item.isDisabled) return;
        logger.info("ChatCommandMenu: item activated via click", { id: item.id });
        item.onSelect?.();
        if (!item.keepOpenOnSelect) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if (item.isDisabled) return;
          item.onSelect?.();
          if (!item.keepOpenOnSelect) onClose();
        }
      }}
      onMouseEnter={() => {
        if (item.isDisabled) return;
        const idx = actionItems.findIndex((a) => a.id === item.id);

        if (idx !== -1) setSelectedIndex(idx);
      }}
    >
      {/* Leading icon */}
      <Icon className="w-4 h-4 text-default-500 shrink-0" icon={item.icon} />

      {/* Label — skill rows get /{key} + description; rows with a plain
          description (e.g. workflows) get name + description; otherwise label. */}
      {item.skillKey ? (
        <span className="flex-1 flex flex-col gap-0.5 min-w-0">
          <span className="text-sm font-medium text-default-800 truncate">/{item.skillKey}</span>
          {item.skillDescription && (
            <span className="text-xs text-default-400 truncate">{item.skillDescription}</span>
          )}
        </span>
      ) : item.description ? (
        <span className="flex-1 flex flex-col gap-0.5 min-w-0">
          <span className="text-sm text-default-700 truncate">{item.label}</span>
          <span className="text-xs text-default-400 truncate">{item.description}</span>
        </span>
      ) : (
        <span className="flex-1 text-sm text-default-700">{item.label}</span>
      )}

      {/* Trailing indicator: tick when current, chevron otherwise */}
      {item.isCurrent ? (
        <Icon
          aria-label={t("chatCommandMenu.workflows.active")}
          className="w-4 h-4 text-success-500 shrink-0"
          icon="solar:check-circle-bold"
        />
      ) : !item.isDisabled ? (
        <Icon className="w-3 h-3 text-default-300 shrink-0" icon="solar:alt-arrow-right-linear" />
      ) : null}
    </div>
  );

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  return (
    <div
      ref={containerRef}
      aria-label={t("chatCommandMenu.header")}
      className="fixed z-50 bg-content1 border border-divider rounded-xl shadow-lg min-w-70 max-w-xs overflow-hidden"
      role="menu"
      style={{
        top: position.top,
        left: position.left,
        transform: "translateY(-100%)",
      }}
      tabIndex={-1}
      onMouseDown={(e) => e.preventDefault()}
    >
      {/* ------------------------------------------------------------------ */}
      {/* Header — main view shows the title; the Workflows sub-view shows a    */}
      {/* back affordance. A close (X) sits on the right so touch users can      */}
      {/* dismiss the menu (Esc is unavailable on mobile).                      */}
      {/* ------------------------------------------------------------------ */}
      <div className="flex items-stretch border-b border-divider">
        {view === "workflows" ? (
          <button
            aria-label={t("chatCommandMenu.workflows.back")}
            className="flex flex-1 min-w-0 items-center gap-2 px-3 py-2 text-left hover:bg-default-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
            data-id="workflows-back"
            type="button"
            onClick={() => setView("main")}
          >
            <Icon
              className="w-4 h-4 text-default-500 shrink-0"
              icon="solar:alt-arrow-left-linear"
            />
            <span className="text-xs font-semibold text-default-600 truncate">
              {workflowsLabel || t("chatCommandMenu.sections.workflows")}
            </span>
          </button>
        ) : (
          <div className="flex flex-1 min-w-0 items-center px-3 py-2">
            <span className="text-xs font-semibold text-default-600">
              {t("chatCommandMenu.header")}
            </span>
          </div>
        )}

        {/* Close (X) — pointer/touch dismissal */}
        <button
          aria-label={t("Close")}
          className="flex shrink-0 items-center justify-center px-3 min-h-11 text-default-400 hover:text-default-600 hover:bg-default-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
          data-id="command-menu-close"
          type="button"
          onClick={onClose}
        >
          <Icon className="w-4 h-4" icon="solar:close-circle-linear" />
        </button>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Body — sections (main) or the workflow list + footer (sub-view)      */}
      {/* ------------------------------------------------------------------ */}
      <div className="overflow-y-auto max-h-72">
        {view === "workflows"
          ? workflowSubItems.map((item) => {
              const actionIndex = actionItems.findIndex((a) => a.id === item.id);
              const isActive = actionIndex !== -1 && actionIndex === selectedIndex;

              return (
                <React.Fragment key={item.id}>
                  {item.dividerBefore && <div className="my-1 h-px bg-divider" />}
                  {renderActionRow(item, isActive)}
                </React.Fragment>
              );
            })
          : sections.map((section) => {
              // Skip sections that have no visible items.
              if (section.items.length === 0) return null;

              return (
                <div key={section.id}>
                  {/* Section label (omitted for headerless drill-in rows) */}
                  {section.label && (
                    <div className="px-3 pt-2 pb-1">
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-default-400">
                        {section.label}
                      </span>
                    </div>
                  )}

                  {/* Section items */}
                  {section.items.map((item) => {
                    if (item.isToggle) {
                      return renderToggleRow(item);
                    }

                    const actionIndex = actionItems.findIndex((a) => a.id === item.id);
                    const isActive = actionIndex !== -1 && actionIndex === selectedIndex;

                    return renderActionRow(item, isActive);
                  })}
                </div>
              );
            })}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Footer                                                               */}
      {/* ------------------------------------------------------------------ */}
      <div className="px-3 py-2 border-t border-divider text-xs text-default-400 flex items-center justify-between gap-2">
        {/* Keyboard hints */}
        <div className="flex items-center gap-3 shrink-0">
          <span className="flex items-center gap-1">
            <KbdHint>↑↓</KbdHint>
            {t("chatCommandMenu.footer.navigate")}
          </span>
          <span className="flex items-center gap-1">
            <KbdHint>Enter</KbdHint>
            {t("chatCommandMenu.footer.select")}
          </span>
          <span className="flex items-center gap-1">
            <KbdHint>Esc</KbdHint>
            {t("chatCommandMenu.footer.close")}
          </span>
        </div>

        {/* Settings link: icon-only with the label in a tooltip to avoid clipping */}
        <Tooltip>
          <TooltipTrigger>
            <Link
              aria-label={t("chatCommandMenu.footer.manageInSettings")}
              className="text-primary hover:opacity-80 flex items-center shrink-0"
              href="/settings#general"
              size="sm"
            >
              <Icon className="w-4 h-4" icon="solar:settings-linear" />
            </Link>
          </TooltipTrigger>
          <TooltipContent showArrow>{t("chatCommandMenu.footer.manageInSettings")}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};

export default ChatCommandMenu;
